/** @jsxImportSource @emotion/react */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useSearchParams } from 'react-router-dom';
import { useMessageBar } from 'hooks/useMessageBar';
import Tab from '@mui/material/Tab';
import Tabs from '@mui/material/Tabs';
import PropTypes from 'prop-types';
import {
  InputBox,
  SearchBox,
  SearchBoxRow,
  SearchCols,
  SearchRows,
} from 'components/layouts/SearchBox';
import { ComboBox, MultiComboBox } from 'components/selects/ComboBox';
import { Code } from 'models/common/CommonCode';
import { getCommonCodeNames } from 'apis/admin/CommonCode';
import SearchBoxButtons from 'components/buttons/SearchBoxButtons';
import { tabs } from 'components/layouts/Tab';
import { SetupProject } from 'models/setup/SetupProject';
import { findDisplayProject } from 'apis/setup/SetupProject';
import {
  SetupSchedule,
  SetupScheduleCondition,
  SetupScheduleReqTypes,
} from 'models/setup/SetupSchedule';
import ScheduleRegistContent from './schedule/ScheduleRegistContent';
import ScheduleStatusContent from './schedule/ScheduleStatusContent';
import ScheduleStatusDetailContent from './schedule/ScheduleStatusDetailContent';
import {
  checkAvailableRequest,
  findExcpByElmPrjId,
  findRequestApproved,
} from 'apis/setup/SetupSchedule';
import ApproveRequestModal from '../approves/ApproveRequestModal';
import { ApproveRequestPageType } from '../approves/ApproveRequestDetailCase';
import { PageMode } from 'models/common/Common';
import { findApprovalPageId } from 'apis/approves/approves';
import { useLoading } from 'components/process/Loading';
import useSessionStore from 'stores/useSessionStore';

/**
 * @desc 설비셋업관리 > 프로젝트 셋업관리 > 프로젝트 일정 조회 화면
 * @author
 * -------------------------------------------------------------------------------------
 *  Date.       author         Description
 * -------------------------------------------------------------------------------------
 * 2025.10.24   jshwang12     신규 생성
 * 2026.04.02   jshwang12     업데이트요청 + 임시저장이 함께 있는 경우 요청유형이 다르면 새로운 요청 생성을 위해 분리, 동일한 요청유형이면 이전 작성하던 문서 열기 (ex. 일정등록|업데이트요청 + 일정변경 | 임시저장)
 */
interface PageLocationState {
  elmPrjId?: string;
  excpId?: string;
  aprReqId?: string;
  type?: string;
}
const SetupSchedulePage = () => {
  const { t } = useTranslation();
  const { openMessageBar } = useMessageBar();
  const { openLoading } = useLoading();
  const { state } = useLocation();
  const tabRegRef = useRef<any>();
  const tabStatRef = useRef<any>();
  const tabStatdRef = useRef<any>();
  const [locationState, setLocationState] = useState<PageLocationState>(useLocation().state);
  const conditionRef = useRef<SetupScheduleCondition>();
  const [errors, setErrors] = useState<any>({});
  const [code, setCode] = useState<any>();
  const [isOpenRequestModal, setOpenRequestModal] = useState<boolean>(false);
  const [requestModalCondition, setRequestModalCondition] = useState<any>({});
  const [searchParams] = useSearchParams();
  const userSession = useSessionStore();

  const defaultCondition = {
    bildPlntCds: [],
    aprTplIds: [],
    elmPrjId: '',
    excpId: userSession?.ensolYn === 'Y' ? '' : userSession?.deptCd,
  };
  const [prjMasterData, setPrjMasterData] = useState<SetupProject[]>([]);
  const [prjExcpData, setPrjExcpData] = useState<SetupSchedule[]>([]);
  const [condition, setCondition] = useState<SetupScheduleCondition>(defaultCondition);
  const [currentTabIndex, setCurrentTabIndex] = useState<number>(0);

  const tabMenus = useMemo(() => {
    return [
      { id: 'REG', name: t('setup.label.일정등록/조회', '일정등록/조회') },
      { id: 'STAT', name: t('setup.label.일정현황', '일정현황') },
      { id: 'STATD', name: t('setup.label.일정현황 상세', '일정현황 상세') },
    ];
  }, []);

  const optionsPrjMaster = useMemo(() => {
    return (prjMasterData || [])
      .filter((o) =>
        (condition?.bildPlntCds || []).length > 0
          ? condition.bildPlntCds.includes(o.bildPlntCd)
          : true
      )
      .reduce(
        (acc, cur) => [
          ...acc,
          {
            cmnCd: cur.elmPrjId,
            cmnCdNm: cur.elmPrjNm,
          } as Code,
        ],
        [] as Code[]
      );
  }, [prjMasterData, condition?.bildPlntCds]);

  const optionsPrjExcp = useMemo(() => {
    return (prjExcpData || []).reduce(
      (acc, cur) => [
        ...acc,
        {
          cmnCd: cur.excpId,
          cmnCdNm: cur.excpNm,
        } as Code,
      ],
      [] as Code[]
    );
  }, [prjExcpData]);

  useEffect(() => {
    if (condition?.elmPrjId) {
      handleSearch(condition);
      // 협력업체인 경우 고정
      const excpId = userSession?.ensolYn === 'Y' ? '' : userSession?.deptCd;
      findExcpByElmPrjId(condition?.elmPrjId, excpId).then((result: SetupSchedule[]) => {
        setPrjExcpData(result);
      });
    } else {
      setPrjExcpData([]);
    }
  }, [condition?.elmPrjId, condition?.excpId, userSession?.ensolYn]);

  useEffect(() => {
    findDisplayProject().then((result: SetupProject[]) => {
      // 조회조건 자동설정
      // TODO 1. PJT 담당자로 지정된 사용자 : 본인이 담당자로 지정된 PJT의 플랜트, 프로젝트명이 Default로 지정되며, 담당자로 지정된 PJT가 복수일 경우 가장 최근 등록된 PJT 기준으로 자동 설정
      // 2. 그 외 사용자: 가장 최근 등록된 PJT 기준으로 자동설정
      if (
        (result || []).length &&
        result[0]?.elmPrjId &&
        !searchParams?.get('elmPrjId') &&
        !locationState?.elmPrjId &&
        !userSession?.elmPrjId
      ) {
        handleOnChange('elmPrjId', result[0].elmPrjId);
      }
      setPrjMasterData(result);
    });
    getCommonCodes();
  }, []);

  useEffect(() => {
    const aprReqId = searchParams?.get('aprReqId') || locationState?.aprReqId;
    const type = searchParams?.get('type') || locationState?.type;
    const elmPrjId =
      searchParams?.get('elmPrjId') || locationState?.elmPrjId || userSession?.elmPrjId;
    if (window.location.pathname === '/setup/schedule') {
      if (aprReqId) {
        openLoading(true);
        findApprovalPageId(aprReqId)
          .then((pageId) => {
            openLoading(false);
            if (pageId) {
              openRequestReadModal(pageId, aprReqId);
            } else {
              openMessageBar({
                type: 'error',
                content: String(t('setup.label.접근권한이 없습니다.', '접근권한이 없습니다.')),
              });
            }
          })
          .finally(() => openLoading(false));
      }
      if (type === 'SCHD_INFO') {
        // 일정현황 탭으로 이동
        changeCurrentTabIndex('STAT');
      }
      if (elmPrjId) {
        handleOnChange('elmPrjId', elmPrjId);
      }
    }
  }, []);

  useEffect(() => {
    if (locationState?.elmPrjId) {
      handleOnChange('elmPrjId', locationState?.elmPrjId);
    }
    if (locationState?.excpId) {
      handleOnChange('excpId', locationState?.excpId);
    }
  }, [locationState]);

  useEffect(() => {
    if (state) {
      setLocationState(state);
    }
  }, [state]);

  useEffect(() => {
    if (conditionRef.current) {
      searchTabContent();
    }
  }, [currentTabIndex]);

  const changeCurrentTabIndex = (tabId) => {
    if (tabId && (tabMenus || []).length) {
      // 일치하는 유형이 없는 경우 첫번째 탭
      setCurrentTabIndex(
        Math.max(
          0,
          (tabMenus || []).findIndex((o) => o.id === tabId)
        )
      );
    }
  };

  const getCommonCodes = async () => {
    const bildPlntCd: Code[] = await getCommonCodeNames('FA_SU_PLNT_CD'); // 플랜트코드
    const aprReqPageTpCd: Code[] = await getCommonCodeNames('APR_REQ_PAGE_TP_CD');
    const aprTplId = (aprReqPageTpCd || [])
      .filter((o) => SetupScheduleReqTypes.includes(o.cmnCd || '') && o.optValCtn1)
      .reduce(
        (acc, cur) => [
          ...acc,
          {
            cmnCd: cur.optValCtn1,
            cmnCdNm: cur.cmnCdNm,
          },
        ],
        [] as Code[]
      );
    setCode({
      bildPlntCd: bildPlntCd,
      aprTplId: aprTplId,
    });
  };

  const handleOnChange = (name, value) => {
    setCondition((prev) => ({
      ...prev,
      [name]: name === 'excpId' && userSession?.ensolYn !== 'Y' ? userSession?.deptCd : value,
    }));
    setErrors((prev) => ({
      ...prev,
      [name]: false,
    }));
  };

  const validate = () => {
    const err = {};
    // 프로젝트명
    if (!condition?.elmPrjId) {
      Object.assign(err, { elmPrjId: true });
    }
    setErrors(err);
    if (!Object.keys(err).filter((k) => err[k]).length) {
      return true;
    }
    return false;
  };

  const handleSearch = (condition) => {
    if (!validate()) {
      return;
    }
    conditionRef.current = condition;
    searchTabContent();
  };

  const searchTabContent = () => {
    if ((tabMenus || []).length) {
      if (tabMenus[currentTabIndex].id === 'REG') {
        tabRegRef.current?.search(conditionRef.current);
      } else if (tabMenus[currentTabIndex].id === 'STAT') {
        tabStatRef.current?.search(conditionRef.current);
      } else if (tabMenus[currentTabIndex].id === 'STATD') {
        tabStatdRef.current?.search(conditionRef.current);
      }
    }
  };

  const openRequestModal = async (
    pageId: ApproveRequestPageType | string,
    elmPrjId: string,
    excpId: string,
    aprReqId?: string,
    checkedItems?: any[]
  ) => {
    const requestList = (checkedItems || []).reduce(
      (acc, cur) => [
        ...acc,
        {
          excpId: cur['EXCP_ID'],
          tgtEqpId: cur['TGT_EQP_ID'],
          spTpCd: cur['SP_TP_CD'],
        },
      ],
      []
    );
    const check = await checkAvailableRequest(pageId, elmPrjId, excpId, aprReqId, requestList);
    if (check.successOrNot === 'N') {
      openMessageBar({
        type: 'error',
        content:
          check.data && typeof check.data === 'string'
            ? check.data
            : t('setup.label.조회 중 오류가 발생했습니다.', '조회 중 오류가 발생했습니다.'),
      });
      return;
    }
    const aprReqPageTpCd: Code[] = await getCommonCodeNames('APR_REQ_PAGE_TP_CD');
    const aprTplIds = (aprReqPageTpCd || [])
      .filter((o) => pageId === o.cmnCd && o.optValCtn1)
      .reduce(
        (acc, cur) => [
          ...acc,
          {
            cmnCd: cur.optValCtn1,
            cmnCdNm: cur.cmnCdNm,
          },
        ],
        [] as Code[]
      );
    let aprTplIdByPageId = 'NN';
    if ((aprTplIds || []).length > 0 && aprTplIds[0].cmnCd) {
      aprTplIdByPageId = aprTplIds[0].cmnCd;
    }
    if (check?.data?.readOnlyYn === 'Y') {
      if (aprReqId) {
        openRequestReadModal(pageId, aprReqId);
      } else {
        openMessageBar({
          type: 'warning',
          content:
            check.data && typeof check.data === 'string'
              ? check.data
              : t('setup.label.결재 문서가 없습니다.', '결재 문서가 없습니다.'),
        });
      }
      return;
    }
    const rAprReqId = check?.data?.aprReqId;
    const rPageId = check?.data?.aprReqTpCd;
    const rAprTplId = check?.data?.aprTplId;
    findRequestApproved(elmPrjId).then((result) => {
      if ((result || []).length < 1) {
        openMessageBar({
          type: 'warning',
          content: t(
            'setup.label.프로젝트 담당자 정보가 없습니다.',
            '프로젝트 담당자 정보가 없습니다.'
          ),
        });
        return;
      }
      setRequestModalCondition({
        pageId: rPageId || pageId,
        elmPrjId: elmPrjId,
        excpId: excpId,
        // [26.04.02] 업데이트요청 + 임시저장이 함께 있는 경우 요청유형이 다르면 새로운 요청 생성을 위해 분리, 동일한 요청유형이면 이전 작성하던 문서 열기 (ex. 일정등록|업데이트요청 + 일정변경 | 임시저장)
        aprReqId: aprReqId || rAprTplId === aprTplIdByPageId ? rAprReqId : '',
        approvalAppd: result,
        requestList: requestList,
      });
      setOpenRequestModal(true);
    });
  };

  const openRequestReadModal = (pageId: ApproveRequestPageType | string, aprReqId: string) => {
    setRequestModalCondition({
      pageId: pageId,
      mode: PageMode.READ,
      aprReqId: aprReqId,
    });
    setOpenRequestModal(true);
  };

  return (
    <>
      <SearchBox>
        <SearchBoxRow>
          <InputBox>
            <SearchRows>
              <SearchCols className={currentTabIndex === 0 ? 'width25' : ''}>
                <label>
                  <span>{String(t('setup.label.플랜트', '플랜트'))}</span>
                </label>
                <MultiComboBox
                  id="bildPlntCd"
                  placeholder={String(t('com.label.전체', '전체'))}
                  options={code?.bildPlntCd || []}
                  defaultValue={condition.bildPlntCds || []}
                  onChange={(value) => {
                    handleOnChange('bildPlntCds', value);
                    if (
                      !(prjMasterData || []).filter(
                        (o) =>
                          condition?.elmPrjId === o.elmPrjId &&
                          ((value || []).length < 1 || (value || []).includes(o.bildPlntCd || ''))
                      ).length
                    ) {
                      handleOnChange('elmPrjId', '');
                      handleOnChange('excpId', '');
                    }
                  }}
                  className="width25"
                />
              </SearchCols>
              <SearchCols className={currentTabIndex === 0 ? 'width25' : ''}>
                <label>
                  <span className="dot">{String(t('setup.label.프로젝트명', '프로젝트명'))}</span>
                </label>
                <ComboBox
                  placeholder={String(t('com.label.선택', '선택'))}
                  options={optionsPrjMaster}
                  defaultValue={condition?.elmPrjId}
                  onChange={(value) => {
                    handleOnChange('elmPrjId', value);
                    handleOnChange('excpId', '');
                  }}
                  isError={errors?.elmPrjId}
                  msgError={String(
                    t('setup.label.프로젝트명을 선택해 주세요.', '프로젝트명을 선택해 주세요.')
                  )}
                />
              </SearchCols>
              <SearchCols className={currentTabIndex === 0 ? 'width25' : ''}>
                <label>
                  <span>{String(t('setup.label.협력업체명', '협력업체명'))}</span>
                </label>
                {userSession?.ensolYn === 'Y' ? (
                  <ComboBox
                    placeholder={String(t('com.label.전체', '전체'))}
                    options={optionsPrjExcp}
                    defaultValue={condition?.excpId}
                    onChange={(value) => {
                      handleOnChange('excpId', value);
                    }}
                  />
                ) : (
                  <div className="txt">
                    <span>{userSession?.deptNm}</span>
                  </div>
                )}
              </SearchCols>
              {currentTabIndex === 0 && (
                <SearchCols className="width25">
                  <label>
                    <span>{String(t('setup.label.요청유형', '요청유형'))}</span>
                  </label>
                  <MultiComboBox
                    id="aprTplId"
                    placeholder={String(t('com.label.전체', '전체'))}
                    options={code?.aprTplId || []}
                    defaultValue={condition.aprTplIds || []}
                    onChange={(value) => {
                      handleOnChange('aprTplIds', value);
                    }}
                    className="width25"
                  />
                </SearchCols>
              )}
            </SearchRows>
          </InputBox>
          <SearchBoxButtons
            defaultCondition={defaultCondition}
            setReset={setCondition}
            onSearch={() => handleSearch(condition)}
          />
        </SearchBoxRow>
      </SearchBox>

      <div css={tabs.wrap} style={{ marginTop: '24px' }}>
        <Tabs
          value={currentTabIndex}
          onChange={(e, newValue) => setCurrentTabIndex(newValue)}
          css={tabs.tab}
        >
          {tabMenus.map((o, idx) => (
            <Tab
              key={o.id}
              label={o.name}
              id={`setup-schedule-tab-${idx}`}
              aria-controls={`setup-schedule-tabpanel-${idx}`}
            />
          ))}
        </Tabs>
      </div>
      <TabPanel value={currentTabIndex} index={currentTabIndex}>
        {currentTabIndex === 0 ? (
          <ScheduleRegistContent
            ref={tabRegRef}
            onScheduleRegist={(aprReqTpCd, elmPrjId, excpId, aprReqId) => {
              openRequestModal(aprReqTpCd, elmPrjId, excpId, aprReqId);
            }}
            onReadRequest={(aprReqTpCd, aprReqId) => {
              openRequestReadModal(aprReqTpCd, aprReqId);
            }}
          />
        ) : currentTabIndex === 1 ? (
          <ScheduleStatusContent
            ref={tabStatRef}
            onScheduleChange={(elmPrjId, excpId, checkedItems, aprReqId) => {
              openRequestModal(
                ApproveRequestPageType.SETUP_SCHD_CHG,
                elmPrjId,
                excpId,
                aprReqId,
                checkedItems
              );
            }}
          />
        ) : (
          <ScheduleStatusDetailContent ref={tabStatdRef} />
        )}
      </TabPanel>

      {isOpenRequestModal && (
        <ApproveRequestModal
          open={isOpenRequestModal}
          close={() => {
            handleSearch(condition);
            setOpenRequestModal(false);
          }}
          pageId={requestModalCondition?.pageId}
          aprReqId={requestModalCondition?.aprReqId}
          condition={{
            elmPrjId: requestModalCondition?.elmPrjId,
            excpId: requestModalCondition?.excpId,
            approvalAppd: requestModalCondition?.approvalAppd,
            mode: requestModalCondition?.mode,
            requestList: requestModalCondition?.requestList,
          }}
        />
      )}
    </>
  );
};

TabPanel.propTypes = {
  children: PropTypes.node,
  index: PropTypes.number.isRequired,
  value: PropTypes.number.isRequired,
};

function TabPanel(props: { [x: string]: any; children: any; value: any; index: any }) {
  const { children, value, index } = props;

  return (
    <div role="tabpanel" hidden={value !== index} id={`setup-schedule-tabpanel-${index}`}>
      {value === index && <div>{children}</div>}
    </div>
  );
}

export default SetupSchedulePage;
